import React, { useState, useMemo } from 'react';
import { SIMULATOR_PRESETS } from '../../data/ecoContent';
import { DoubleBezelCard } from '../common/DoubleBezelCard';

export const BiosphereSimulator: React.FC = () => {
  const [activePreset, setActivePreset] = useState(SIMULATOR_PRESETS[0]);
  const [drones, setDrones] = useState(activePreset.droneSwarmUnits);
  const [oceanAlkalinity, setOceanAlkalinity] = useState(activePreset.oceanAlkalinityPct);
  const [cleanEnergy, setCleanEnergy] = useState(activePreset.microgridCleanRatio);

  const selectPreset = (preset: typeof SIMULATOR_PRESETS[0]) => {
    setActivePreset(preset);
    setDrones(preset.droneSwarmUnits);
    setOceanAlkalinity(preset.oceanAlkalinityPct);
    setCleanEnergy(preset.microgridCleanRatio);
  };

  const calculatedOutputs = useMemo(() => {
    const droneRatio = drones / 20000;
    const oceanRatio = oceanAlkalinity / 50;
    const cleanRatio = cleanEnergy / 100;

    const drawdownGt = +((droneRatio * 0.85 + oceanRatio * 1.4) * cleanRatio).toFixed(2);
    const tempDelta = -(drawdownGt * 0.12).toFixed(3);
    const canopyArea = +(drones * 0.0013 * cleanRatio).toFixed(1);
    const bioIndex = +Math.min(99.8, 70 + (droneRatio * 14 + oceanRatio * 10)).toFixed(1);

    return {
      drawdownGt,
      tempDelta,
      canopyArea,
      bioIndex,
    };
  }, [drones, oceanAlkalinity, cleanEnergy]);

  return (
    <section
      id="simulator"
      className="relative py-24 sm:py-32 px-4 sm:px-6 z-10 border-t border-carbon-900/10 bg-alabaster-100"
    >
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono text-chlorophyll-deep tracking-widest uppercase mb-2">
              <span className="w-2 h-2 bg-chlorophyll inline-block" />
              <span>05 // INTERACTIVE LABORATORY</span>
            </div>
            <h2 className="font-serif font-black text-4xl sm:text-5xl text-carbon-900 tracking-tight">
              Model planetary equilibrium.
            </h2>
          </div>
          <div className="text-xs font-mono text-carbon-900/60">
            CONTINENTAL MASS BALANCE COMPUTATION ENGINE
          </div>
        </div>

        {/* Preset Selector Tabs */}
        <div className="flex flex-wrap items-center gap-2.5">
          {SIMULATOR_PRESETS.map((preset) => {
            const isActive = activePreset.id === preset.id;
            return (
              <button
                key={preset.id}
                onClick={() => selectPreset(preset)}
                className={`px-4 py-2 font-mono text-xs transition-all border ${
                  isActive
                    ? 'bg-carbon-900 text-alabaster-100 border-carbon-900 font-bold shadow-sm'
                    : 'bg-white text-carbon-900/70 border-carbon-900/15 hover:border-chlorophyll'
                }`}
                data-cursor="interactive"
              >
                <span>{preset.name}</span>
              </button>
            );
          })}
        </div>

        {/* Sandbox Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sliders Controller */}
          <div className="lg:col-span-6">
            <DoubleBezelCard enableTilt={false} glowColor="emerald" className="p-6 sm:p-8 space-y-8">
              <div className="border-b border-carbon-900/10 pb-4">
                <div className="font-mono text-[10px] text-carbon-900/40 uppercase tracking-widest mb-1">
                  TACTICAL INPUT CONTROLS
                </div>
                <h3 className="font-serif font-bold text-xl text-carbon-900 mb-1">
                  Simulation Parameters
                </h3>
                <p className="text-xs font-sans text-carbon-900/60">
                  {activePreset.summary}
                </p>
              </div>

              {/* Slider 1 */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-carbon-900 font-medium">AUTONOMOUS SWARMS</span>
                  <span className="text-chlorophyll-deep font-bold font-mono">{drones.toLocaleString()} UNITS</span>
                </div>
                <input
                  type="range"
                  min="1000"
                  max="50000"
                  step="500"
                  value={drones}
                  onChange={(e) => setDrones(+e.target.value)}
                  className="w-full h-1.5 bg-alabaster-300 rounded-none appearance-none cursor-pointer accent-[#059669]"
                />
                <div className="flex justify-between text-[10px] font-mono text-carbon-900/40">
                  <span>1,000 (REGIONAL)</span>
                  <span>50,000 (CONTINENTAL)</span>
                </div>
              </div>

              {/* Slider 2 */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-carbon-900 font-medium">OCEAN ALKALINITY DISPERSION</span>
                  <span className="text-ozone-deep font-bold font-mono">{oceanAlkalinity}% SINK</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={oceanAlkalinity}
                  onChange={(e) => setOceanAlkalinity(+e.target.value)}
                  className="w-full h-1.5 bg-alabaster-300 rounded-none appearance-none cursor-pointer accent-[#0284C7]"
                />
                <div className="flex justify-between text-[10px] font-mono text-carbon-900/40">
                  <span>10% (COASTAL)</span>
                  <span>100% (PELAGIC GYRES)</span>
                </div>
              </div>

              {/* Slider 3 */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-carbon-900 font-medium">GEOTHERMAL / SOLAR RATIO</span>
                  <span className="text-solar-amber font-bold font-mono">{cleanEnergy}% BASELOAD</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="100"
                  step="1"
                  value={cleanEnergy}
                  onChange={(e) => setCleanEnergy(+e.target.value)}
                  className="w-full h-1.5 bg-alabaster-300 rounded-none appearance-none cursor-pointer accent-[#D97706]"
                />
                <div className="flex justify-between text-[10px] font-mono text-carbon-900/40">
                  <span>50% (HYBRID)</span>
                  <span>100% (ZERO PARASITIC)</span>
                </div>
              </div>
            </DoubleBezelCard>
          </div>

          {/* Real-time Telemetry Outputs */}
          <div className="lg:col-span-6 flex flex-col justify-between gap-4">
            <div className="grid grid-cols-2 gap-4 h-full">
              {/* Output 1 */}
              <DoubleBezelCard enableTilt={false} glowColor="emerald" className="p-6 flex flex-col justify-between">
                <div className="text-[10px] font-mono text-chlorophyll-deep uppercase tracking-widest">
                  DRAWDOWN CADENCE
                </div>
                <div className="text-3xl sm:text-4xl font-serif font-bold text-carbon-900">
                  {calculatedOutputs.drawdownGt} <span className="text-xs font-mono font-normal text-chlorophyll-deep">Gt / yr</span>
                </div>
                <div className="text-[11px] font-mono text-carbon-900/50">
                  Subterranean mineralized CO2 mass.
                </div>
              </DoubleBezelCard>

              {/* Output 2 */}
              <DoubleBezelCard enableTilt={false} glowColor="ozone" className="p-6 flex flex-col justify-between">
                <div className="text-[10px] font-mono text-ozone-deep uppercase tracking-widest">
                  TEMP STABILIZATION
                </div>
                <div className="text-3xl sm:text-4xl font-serif font-bold text-ozone-deep">
                  {calculatedOutputs.tempDelta} <span className="text-xs font-mono font-normal text-carbon-900/50">°C</span>
                </div>
                <div className="text-[11px] font-mono text-carbon-900/50">
                  Calculated surface cooling delta.
                </div>
              </DoubleBezelCard>

              {/* Output 3 */}
              <DoubleBezelCard enableTilt={false} glowColor="solar" className="p-6 flex flex-col justify-between">
                <div className="text-[10px] font-mono text-solar-amber uppercase tracking-widest">
                  CANOPY RESTORED
                </div>
                <div className="text-3xl sm:text-4xl font-serif font-bold text-carbon-900">
                  {calculatedOutputs.canopyArea} <span className="text-xs font-mono font-normal text-solar-amber">Mha</span>
                </div>
                <div className="text-[11px] font-mono text-carbon-900/50">
                  Dense biodiversity forest area.
                </div>
              </DoubleBezelCard>

              {/* Output 4 */}
              <DoubleBezelCard enableTilt={false} glowColor="emerald" className="p-6 flex flex-col justify-between">
                <div className="text-[10px] font-mono text-chlorophyll-deep uppercase tracking-widest">
                  BIODIVERSITY RECOVERY
                </div>
                <div className="text-3xl sm:text-4xl font-serif font-bold text-chlorophyll-deep">
                  {calculatedOutputs.bioIndex}%
                </div>
                <div className="text-[11px] font-mono text-carbon-900/50">
                  Audited species abundance score.
                </div>
              </DoubleBezelCard>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
